import { React, useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { Facebook, Instagram, Sparkles, Moon, MessageCircle, Lock, Mail, Send, ArrowRight } from 'lucide-react';
import { 
  Brain, 
  Palette,
  Star,
  Quote,
  Users,
  Camera,
  Eye,
  Waves,
  Flame
} from 'lucide-react';
import { 
  FaImage, 
  FaCalendar, 
  FaShareAlt, 
  FaBell, 
  FaEdit, 
  FaThLarge, 
  FaThermometerThreeQuarters
} from 'react-icons/fa';
import { useNavigate,Link } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { sendEmail } from '../utils/email';
import hotjar from '@hotjar/browser';
import { addDoc, collection,serverTimestamp } from 'firebase/firestore';
import { Download, Smartphone } from 'lucide-react';

const AnimatedBackground = () => (
  <div className="fixed inset-0 -z-10">
    <div className="absolute top-0 left-0 w-full h-full">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className={`
            absolute rounded-full mix-blend-screen filter blur-xl opacity-30 animate-pulse
            ${i % 2 === 0 ? 'bg-purple-500' : 'bg-pink-500'}
          `}
          style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            width: `${Math.random() * 400 + 200}px`,
            height: `${Math.random() * 400 + 200}px`,
            animationDelay: `${i * 0.5}s`,
            animationDuration: `${Math.random() * 10 + 10}s`
          }}
        />
      ))}
    </div>
  </div>
);

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const HeroSection = () => (
  <div className="relative pt-32 pb-16 text-center overflow-hidden">
    <div className="mx-auto max-w-7xl px-6">
      <div className="relative animate-pulse">
        <Eye className="absolute -top-12 left-1/4 w-8 h-8 text-purple-400 opacity-50" />
        <Moon className="absolute -top-8 right-1/3 w-6 h-6 text-pink-400 opacity-50" />
        <Waves className="absolute top-0 right-1/4 w-10 h-10 text-indigo-400 opacity-50" />
      </div>
      <h1 className="text-6xl font-bold text-white md:text-7xl lg:text-8xl tracking-wider">
        <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 animate-pulse">
          Dream
        </span>
        <span className="block text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400 animate-pulse" style={{ animationDelay: '0.5s' }}>
          Beyond
        </span>
      </h1>
      <p className="mx-auto mt-6 max-w-2xl text-xl text-purple-100 animate-pulse" style={{ animationDelay: '1s' }}>
        Enter a world where your dreams transform into mesmerizing digital art.
        Let your subconscious mind paint the canvas of reality.
      </p>
      <div className="mt-10 flex justify-center gap-4">
        <button className="relative rounded-full bg-purple-600 px-8 py-3 text-lg font-semibold text-white shadow-lg hover:bg-purple-500 transition-all duration-500 overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <span className="relative flex items-center">
            <Link to="/signup" className="hover:text-gray-300 rounded-lg px-3 py-1 transition">
              Begin the Journey
              <ArrowRight className="ml-2 inline-block h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </span>
        </button>
      </div>
    </div>
  </div>
);

const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="relative flex flex-col items-center p-6 rounded-2xl border border-purple-500/30 bg-purple-900/30 transition-all duration-500 hover:bg-purple-800/30">
    <div className="rounded-full bg-purple-600 p-3 mb-4">
      <Icon className="h-6 w-6 text-white" />
    </div>
    <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
    <p className="text-purple-200 text-center">{description}</p>
  </div>
);

const NewsletterSection = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidEmail(newsletterEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      await addDoc(collection(db, 'newsletter_signups'), {
        email: newsletterEmail,
        signupSource: 'landing_page_newsletter',
        createdAt: serverTimestamp()
      });

      setNewsletterSubmitted(true);
      setNewsletterEmail('');
    } catch (error) {
      console.error('Newsletter signup error:', error);
      alert('Failed to subscribe. Please try again.');
    }
  };

  return (
    <div className="grid gap-8 max-w-5xl mx-auto">
      <div className="bg-purple-900/30 border border-purple-500/30 rounded-2xl p-8">
        <Mail className="h-12 w-12 text-purple-400 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-center text-white mb-4">
          Dreamverse Newsletter
        </h3>
        <form onSubmit={handleNewsletterSubmit} className="space-y-4">
          <input 
            type="email" 
            value={newsletterEmail}
            onChange={(e) => setNewsletterEmail(e.target.value)}
            placeholder="Enter your email" 
            className="w-full px-4 py-2 rounded bg-purple-900/50 text-white placeholder-gray-300"
            required 
          />
          <button 
            type="submit" 
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 rounded hover:opacity-90 transition"
          >
            Subscribe
          </button>
        </form>
        {newsletterSubmitted && (
          <p className="text-green-400 mt-2 text-center">
            Thank you for subscribing!
          </p>
        )}
      </div>
    </div>
  );
};

const SocialProof = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
    <div className="flex flex-col items-center p-6 rounded-2xl border border-purple-500/30 bg-purple-900/30">
      <Users className="h-8 w-8 text-purple-400 mb-2" />
      <span className="text-4xl font-bold text-white">10K+</span>
      <span className="text-purple-200">Active Dreamers</span>
    </div>
    <div className="flex flex-col items-center p-6 rounded-2xl border border-purple-500/30 bg-purple-900/30">
      <Brain className="h-8 w-8 text-purple-400 mb-2" />
      <span className="text-4xl font-bold text-white">50K+</span>
      <span className="text-purple-200">Dreams Generated</span>
    </div>
    <div className="flex flex-col items-center p-6 rounded-2xl border border-purple-500/30 bg-purple-900/30">
      <Camera className="h-8 w-8 text-purple-400 mb-2" />
      <span className="text-4xl font-bold text-white">100K+</span>
      <span className="text-purple-200">Artworks Created</span>
    </div>
  </div>
);

const InstagramFeed = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    {[1, 2, 3, 4].map((index) => (
      <div key={index} className="relative group overflow-hidden rounded-lg">
        <img
          src={`/api/placeholder/200/200`}
          alt={`Instagram post ${index}`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-purple-900 to-transparent opacity-0 group-hover:opacity-90 transition-opacity duration-500 flex items-center justify-center">
          <div className="text-white text-center transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
            <Star className="h-6 w-6 mx-auto mb-2" />
            <span className="text-sm">1.2K Likes</span>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const Testimonial = ({ quote, author, title }) => (
  <div className="relative p-6 rounded-2xl border border-purple-500/30 bg-purple-900/30">
    <Quote className="h-8 w-8 text-purple-400 mb-4" />
    <p className="text-purple-200 mb-4 italic">{quote}</p>
    <div className="flex items-center gap-4">
      <img
        src="/logo.png"
        alt={author}
        className="w-12 h-12 rounded-full border-2 border-purple-500"
      />
      <div>
        <h4 className="text-white font-semibold">{author}</h4>
        <p className="text-purple-300 text-sm">{title}</p>
      </div>
    </div>
  </div>
);

const ImageGallery = () => {
  const dreamGalleryImages = [
    '/lp/pasta.jpg',
    '/lp/fantasy.jpg',
    '/lp/surrealtax.jpg',
    '/lp/berlingirl.jpg',
    '/lp/bikes.jpg',
    '/lp/cosmos.jpg'
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
      {dreamGalleryImages.map((image, index) => (
        <div key={index} className="relative group overflow-hidden rounded-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-30" />
          <img 
            src={image} 
            alt={`Dream artwork ${index}`}
            className="w-full h-64 object-cover transition-all duration-700 group-hover:scale-110 group-hover:rotate-3"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-purple-900 to-transparent opacity-0 group-hover:opacity-90 transition-all duration-500 flex items-end">
            <p className="text-white p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
              Psychedelic Dream Vision {index}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

const PricingCard = ({ title, price, features, isPopular }) => (
  <div className={`
    relative p-8 rounded-2xl border transition-all duration-500
    ${isPopular ? 'border-purple-500 bg-purple-900/30' : 'border-purple-800 bg-purple-900/20'}
    hover:scale-105 hover:bg-purple-900/40
  `}>
    {isPopular && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-1 rounded-full">
        <span className="text-sm font-semibold text-white">Most Popular</span>
      </div>
    )}
    <h3 className="text-2xl font-bold text-white mb-2">{title}</h3>
    <p className="text-4xl font-bold text-white mb-6">
      ${price}
      <span className="text-lg font-normal text-purple-300">/mo</span>
    </p>
    <ul className="space-y-4 mb-8">
      {features.map((feature, index) => (
        <li key={index} className="flex items-center text-purple-200 group">
          <Flame className="h-5 w-5 text-purple-400 mr-2 group-hover:text-pink-400 transition-colors duration-300" />
          {feature}
        </li>
      ))}
    </ul>
    <button className={`
      w-full py-3 rounded-full font-semibold transition-all duration-500
      ${isPopular 
        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-pink-600 hover:to-purple-600' 
        : 'bg-purple-800/50 text-white hover:bg-purple-700/50'}
    `}>
      <Link to="/signup" className="hover:text-gray-300 rounded-lg px-3 py-1 transition">
        Unlock Your Dreams
      </Link>
    </button>
  </div>
);

const LandingPage = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [profilePic, setProfilePic] = useState('/logo.png');
  const [user, setUser] = useState(null);

  const features = [
    {
      icon: Sparkles,
      title: "Dream to Art",
      description: "Transform your dreams into stunning visual artwork with AI generation"
    },
    {
      icon: Palette,
      title: "Custom Art Styles",
      description: "Select from various artistic styles for your dream visualizations"
    },
    {
      icon: Brain,
      title: "AI Dream Interpretation",
      description: "Get personalized insights (psychological & astrological) into your dreams using advanced AI analysis"
    },
    {
      icon: Moon,
      title: "Dream Journal",
      description: "Keep track of your dreams with our intuitive journaling system forever!"
    },
    {
      icon: Lock,
      title: "Private or Public",
      description: "Choose to keep your dreams private or share them with others"
    },
    {
      icon: FaThLarge,
      title: 'Flexible View Modes',
      description: 'Switch between List, Grid, and Calendar views to explore dreams your way.'
    },
    {
      icon: MessageCircle,
      title: "Community Insights",
      description: "Share and discuss your dreams with a supportive community"
    },
    {
      icon: FaShareAlt,
      title: 'Easy Sharing',
      description: 'Share your dreams across multiple platforms with a single click.'
    },
    {
      icon: FaBell,
      title: 'Real-time Notifications',
      description: 'Stay updated with instant notifications for likes, comments, and interactions.'
    },
    {
      icon: FaEdit,
      title: 'Inline Editing',
      description: 'Quickly edit and refine your dream entries with seamless inline editing.'
    },
    {
      icon: FaThermometerThreeQuarters,
      title: 'More',
      description: 'More features coming soon!'
    }
  ];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'profiles', user.uid));
          if (userDoc.exists()) {
            setUserRole(userDoc.data().role);
            setProfilePic(userDoc.data().profilePic || '/default-profile-pic.png');
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    auth.signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-900 bg-gradient-to-b from-indigo-900 via-purple-900 to-black text-white overflow-hidden w-full">
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl w-full flex-grow">
        <AnimatedBackground />
        <HeroSection />
        
        <div className="py-24 relative">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-5xl font-bold text-center mb-16 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
              Transcend Reality
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </div>
          </div>
        </div>

        <div className="py-24 relative">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-5xl font-bold text-center mb-16 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
              Dream Gallery
            </h2>
            <ImageGallery />
          </div>
        </div>

        <div className="py-24 relative">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-5xl font-bold text-center mb-16 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
              Astral Plans
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <PricingCard
                title="Dreamer"
                price="0"
                features={[
                  "5 dream transmutations/month",
                  "Basic dream interpretation",
                  "Dreamverse access",
                  "Dream chronicles"
                ]}
              />
              <PricingCard
                title="Dream Master"
                price="8"
                features={[
                  "Unlimited dream alchemy",
                  "Advanced consciousness mapping",
                  "Private dream sanctuary",
                  "Priority astral processing",
                  "Reality bending toolkit",
                  "Pure dream frequency"
                ]}
                isPopular={true}
              />
            </div>
          </div>
        </div>

        <div className="py-24 relative">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-5xl font-bold text-center mb-16 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
              Dreamer Stories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Testimonial
                quote="Dreamarium has completely transformed how I connect with my subconscious. The AI interpretations are surprisingly insightful!"
                author="Maya"
                title="Dream Explorer"
              />
              <Testimonial
                quote="The artwork generated from my dreams captures their essence in ways I never thought possible. It's like magic!"
                author="Alex" 
                title="Digital Artist"
              />
            </div>
          </div>
        </div>

        <div className="py-24 relative">
          <div className="max-w-7xl mx-auto px-4">
            <NewsletterSection />
          </div>
        </div>
      </main>
    </div>
  );
};

export default LandingPage;